/**
 * Chat panel.
 *
 * One log for everything, each line tagged with how far it carried: nearby,
 * a room, or the whole office. That mirrors how the server routes messages and
 * is how voice will be scoped too.
 */

import { EVERYONE_SLUG, parseMentions, zoneById, type ChatScope } from '@virtual-office/shared';

import type { Store } from '../state.js';
import { $, clear, el, formatTime } from './dom.js';

export class ChatPanel {
  private readonly log = $<HTMLOListElement>('chat-log');
  private readonly form = $<HTMLFormElement>('chat-form');
  private readonly input = $<HTMLInputElement>('chat-input');
  private readonly scopeSelect = $<HTMLSelectElement>('chat-scope');
  private pinnedToBottom = true;

  constructor(
    private readonly store: Store,
    private readonly onSend: (scope: ChatScope, body: string) => void,
  ) {
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      const body = this.input.value.trim();
      if (!body) return;
      this.onSend(this.scopeSelect.value as ChatScope, body);
      this.input.value = '';
    });

    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.input.blur();
        event.stopPropagation();
      }
    });

    // Only auto-scroll when the reader is already at the bottom.
    this.log.addEventListener('scroll', () => {
      const distance = this.log.scrollHeight - this.log.scrollTop - this.log.clientHeight;
      this.pinnedToBottom = distance < 40;
    });

    this.store.on('chat', () => this.render());
    this.render();
  }

  focus(): void {
    this.input.focus();
  }

  private scopeLabel(scope: ChatScope, channelId: string): string {
    if (scope === 'office') return 'office';
    if (scope === 'local') return 'nearby';
    const zoneId = channelId.startsWith('zone:') ? channelId.slice(5) : null;
    const zone = this.store.map ? zoneById(this.store.map, zoneId) : null;
    return zone?.name.toLowerCase() ?? 'room';
  }

  /**
   * Render a message with its @mentions picked out.
   *
   * Uses the same parser the server acts on, so what looks like a mention is
   * exactly what became one — highlighting a word the server ignored would be
   * worse than not highlighting at all.
   */
  private renderBody(body: string): HTMLElement {
    const wrapper = el('span', { className: 'chat-body' });
    const slugs = new Set(parseMentions(body));
    if (slugs.size === 0) {
      wrapper.textContent = body;
      return wrapper;
    }

    // Split on the same token shape the parser matches, keeping the text
    // between so nothing is lost or reordered.
    const parts = body.split(/(@[a-z0-9][a-z0-9-]{0,31})/gi);
    for (const part of parts) {
      if (part.startsWith('@') && slugs.has(part.slice(1).toLowerCase())) {
        const mention = el('span', { className: 'mention', text: part });
        // A mention that reaches you personally deserves to stand out more.
        if (this.mentionsMe(part.slice(1).toLowerCase())) mention.classList.add('mention-me');
        wrapper.append(mention);
      } else if (part) {
        wrapper.append(document.createTextNode(part));
      }
    }
    return wrapper;
  }

  /** Whether a slug resolves to a group the viewer is actually in. */
  private mentionsMe(slug: string): boolean {
    if (slug === EVERYONE_SLUG) return true;
    const me = this.store.meId;
    if (!me) return false;
    return this.store.groups.some(
      (group) => group.slug === slug && group.memberIds.includes(me),
    );
  }

  private render(): void {
    clear(this.log);
    for (const message of this.store.messages) {
      const line = el('li', { className: `chat-line scope-${message.scope}` });

      const author = el('span', { className: 'chat-author', text: message.authorName });
      author.style.color = message.authorColor;

      line.append(
        el('span', { className: 'chat-time', text: formatTime(message.createdAt) }),
        el('span', {
          className: 'chat-scope',
          text: this.scopeLabel(message.scope, message.channelId),
        }),
        author,
        this.renderBody(message.body),
      );
      this.log.append(line);
    }
    if (this.pinnedToBottom) this.log.scrollTop = this.log.scrollHeight;
  }
}
